'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { usePathname } from 'next/navigation';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useQuery } from '@tanstack/react-query';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { Suspense } from 'react';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export default function Page() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const { me, isInitialized } = useCustomerStore();

  const pathname = usePathname();

  const query = buildProductsQuery({
    order: 'desc',
    orderBy: 'position',
    discounted: true,
    results: 12,
  });

  const { data: products, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS, query, locale, me?.customer.id ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        query,
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
  });

  return (
    <section className={cn('custom-section', 'gap-12 pb-6 pt-12 sm:pb-8 md:pb-10 lg:pb-12')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-4 sm:gap-5 lg:gap-6',
        )}
      >
        <Breadcrumbs
          breadcrumb={{ name: 'Akčné produkty', slug: pathname }}
          className={{
            wrapper: 'items-center justify-center',
            list: 'items-center justify-center',
          }}
        />
        <h2 className={cn('h2', 'w-full text-center font-medium tracking-tight text-foreground')}>
          {t('text3756')}
        </h2>
        {!isLoading && (
          <p className="w-full text-center text-xs sm:text-sm">
            {products?.meta?.total ?? 0} produktov
          </p>
        )}
      </div>
      <div className={cn('custom-container', 'flex flex-col items-start justify-start')}>
        {isLoading ? (
          <div className={cn('flex h-[300px] w-full items-center justify-center')}>
            <Spinner />
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-6 sm:gap-8 lg:gap-12">
            <div
              className={cn(
                'grid w-full grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-y-12 lg:grid-cols-4 lg:gap-x-4 xl:grid-cols-5',
              )}
            >
              {products?.data.map(product => (
                <ProductCard key={`product-${product.id}`} product={product} />
              ))}
            </div>
            <Suspense>
              {products?.meta && <Pagination links={products.meta.links ?? []} />}
            </Suspense>
          </div>
        )}
      </div>
    </section>
  );
}
